import React, { useState } from 'react';
import ReusableForm from '../../components/ReusableForm';
import { solicitarCambioContrasena, validarCodigo, resetearContrasena } from '../../services/Auth/forgotPasswordServer';
import projectLogo from '../../public/Logo.png';
import Sidebar from '../../components/Sidebar';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

const ChangePasswordPage = () => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [nuevaContrasena, setNuevaContrasena] = useState('');
  const [confirmarContrasena, setConfirmarContrasena] = useState('');
  const [tokenTemporal, setTokenTemporal] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  // Sidebar links igual que en HomePage
  const esAdmin = () => {
    const token = localStorage.getItem('authToken');
    if (!token) return false;
    try {
      const decodedToken = jwtDecode(token);
      return decodedToken.rolId === '1';
    } catch (error) {
      return false;
    }
  };
  const esSoporteTecnico = () => {
    const token = localStorage.getItem('authToken');
    if (!token) return false;
    try {
      const decodedToken = jwtDecode(token);
      return decodedToken.rolId === '3';
    } catch (error) {
      return false;
    }
  };
  const links = [
    { to: '/home', label: 'Home' },
    { to: '/crear-tarea', label: 'Crear Tarea' },
    { to: '/tareas', label: 'Ver Tarea' },
    { to: '/cambiar-contrasena', label: 'Cambiar Contraseña' },
  ];
  if (esAdmin()) links.push({ to: '/admin', label: 'Admin Panel' });
  if (esSoporteTecnico()) links.push({ to: '/soporte', label: 'Soporte' });
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    navigate('/auth/login');
  };

  // Paso 1: Solicitar código
  const handleSolicitarCodigo = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    if (!email) {
      setError('Por favor, introduce tu correo electrónico.');
      return;
    }
    setLoading(true);
    try {
      const response = await solicitarCambioContrasena(email);
      setSuccessMessage(response.message || 'Código enviado a tu correo.');
      setStep(2);
    } catch (err) {
      setError(err.message || 'Error al solicitar el cambio de contraseña.');
    } finally {
      setLoading(false);
    }
  };

  // Paso 2: Validar código
  const handleValidarCodigo = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    if (!email || !code) {
      setError('Por favor, introduce tu correo y el código de verificación.');
      return;
    }
    setLoading(true);
    try {
      const result = await validarCodigo({ email, codigo: code });
      if (result && result.tokenTemporal) {
        setTokenTemporal(result.tokenTemporal);
        setSuccessMessage('Código validado correctamente. Ahora puedes establecer una nueva contraseña.');
        setStep(3);
      } else {
        setError(result.message || 'Error al validar el código.');
      }
    } catch (err) {
      setError(err.message || 'Ocurrió un error al validar el código.');
    } finally {
      setLoading(false);
    }
  };

  // Paso 3: Establecer nueva contraseña
  const handleEstablecerContrasena = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    if (!nuevaContrasena || !confirmarContrasena) {
      setError('Por favor, completa ambos campos de contraseña.');
      return;
    }
    if (nuevaContrasena !== confirmarContrasena) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    if (!tokenTemporal) {
      setError('Token temporal no encontrado.');
      return;
    }
    setLoading(true);
    try {
      const result = await resetearContrasena(tokenTemporal, nuevaContrasena);
      if (result.success) {
        setSuccessMessage(result.message || 'Contraseña actualizada exitosamente.');
        setStep(4);
      } else {
        setError(result.message || 'Error al actualizar la contraseña.');
      }
    } catch (err) {
      setError(err.message || 'Ocurrió un error al actualizar la contraseña.');
    } finally {
      setLoading(false);
    }
  };

  // Renderizado condicional por paso
  let formContent;
  if (step === 1) {
    formContent = (
      <ReusableForm
        title="Cambiar mi Contraseña"
        imageSrc={projectLogo}
        error={error}
        successMessage={successMessage}
        handleSubmit={handleSolicitarCodigo}
        submitButtonText={loading ? 'Enviando...' : 'Solicitar Código'}
      >
        <div className="form-group">
          <input
            type="email"
            id="email"
            placeholder="Ingresa tu correo electrónico."
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="form-input"
            disabled={loading}
          />
        </div>
      </ReusableForm>
    );
  } else if (step === 2) {
    formContent = (
      <ReusableForm
        title="Validar Código"
        imageSrc={projectLogo}
        error={error}
        successMessage={successMessage}
        handleSubmit={handleValidarCodigo}
        submitButtonText={loading ? 'Validando...' : 'Validar Código'}
      >
        <div className="form-group">
          <input
            type="email"
            id="email"
            placeholder="Ingresa tu correo electrónico."
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="form-input"
            disabled={loading}
          />
        </div>
        <div className="form-group">
          <input
            type="text"
            id="code"
            placeholder="Ingresa el código de verificación."
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            className="form-input"
            disabled={loading}
          />
        </div>
      </ReusableForm>
    );
  } else if (step === 3) {
    formContent = (
      <ReusableForm
        title="Establecer Nueva Contraseña"
        imageSrc={projectLogo}
        error={error}
        successMessage={successMessage}
        handleSubmit={handleEstablecerContrasena}
        submitButtonText={loading ? 'Actualizando...' : 'Actualizar Contraseña'}
      >
        <div className="form-group">
          <input
            type="password"
            id="nuevaContrasena"
            placeholder="Introduce tu nueva contraseña"
            value={nuevaContrasena}
            onChange={(e) => setNuevaContrasena(e.target.value)}
            required
            className="form-input"
            disabled={loading}
          />
        </div>
        <div className="form-group">
          <input
            type="password"
            id="confirmarContrasena"
            placeholder="Confirma tu nueva contraseña"
            value={confirmarContrasena}
            onChange={(e) => setConfirmarContrasena(e.target.value)}
            required
            className="form-input"
            disabled={loading}
          />
        </div>
      </ReusableForm>
    );
  } else if (step === 4) {
    formContent = (
      <ReusableForm
        title="Contraseña Actualizada"
        imageSrc={projectLogo}
        error={error}
        successMessage={successMessage || '¡Tu contraseña ha sido cambiada exitosamente!'}
        handleSubmit={(e) => e.preventDefault()}
        submitButtonText="Listo"
      >
        <div className="form-group">
          <p>Puedes cerrar esta ventana o navegar a otra sección.</p>
        </div>
      </ReusableForm>
    );
  }

  return (
    <div className="home-container">
      <Sidebar links={links} onLogout={handleLogout} />
      <main className="home-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: 0 }}>
        <div style={{ width: '100%', maxWidth: 420 }}>
          {formContent}
        </div>
      </main>
    </div>
  );
};

export default ChangePasswordPage; 